import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { API, CATEGORY_COLORS } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import {
    Package,
    Boxes,
    AlertTriangle,
    LogIn,
    LayoutDashboard,
    ShieldCheck,
} from "lucide-react";

import PublicNav from "@/components/PublicNav";

// Public endpoint — no auth required
const publicApi = axios.create({ baseURL: API });

export default function PublicStock() {
    const { token } = useAuth();
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState("");
    const [categoryFilter, setCategoryFilter] = useState("all");

    useEffect(() => {
        publicApi
            .get("/public/stock")
            .then((r) => setItems(r.data))
            .finally(() => setLoading(false));
    }, []);

    const categories = useMemo(() => {
        const seen = new Map();
        for (const it of items) {
            if (it.category_id && !seen.has(it.category_id)) {
                seen.set(it.category_id, {
                    id: it.category_id,
                    name: it.category_name,
                    color: it.category_color,
                });
            }
        }
        return [...seen.values()].sort((a, b) => a.name.localeCompare(b.name));
    }, [items]);

    const filtered = items.filter((it) => {
        const q = search.trim().toLowerCase();
        const matchSearch = !q || it.name.toLowerCase().includes(q);
        const matchCat =
            categoryFilter === "all" || it.category_id === categoryFilter;
        return matchSearch && matchCat;
    });

    const totalItems = items.reduce((s, i) => s + i.quantity, 0);
    const totalProducts = items.length;
    const lowStock = items.filter((i) => i.low_stock).length;

    return (
        <div className="min-h-screen bg-[#F7F3E8]">
            {/* Top bar */}
            <header className="sticky top-0 z-20 bg-[#2E3A22] text-[#EDE7D3] border-b border-[#3D4E2E] shadow-sm">
                <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between gap-4 flex-wrap">
                    <div>
                        <div className="text-[10px] uppercase tracking-[0.25em] text-[#C8A75C] font-semibold">
                            Raza Perdida MC
                        </div>
                        <div
                            data-testid="public-brand"
                            className="font-mc text-xl sm:text-2xl font-semibold tracking-tight mt-0.5"
                        >
                            Stock
                        </div>
                    </div>
                    <div className="flex items-center gap-3">
                        <PublicNav />
                        {token ? (
                            <Link to="/dashboard">
                                <Button
                                    data-testid="public-open-dashboard-btn"
                                    className="bg-[#F5EFDA] hover:bg-[#EDE4C0] text-[#2E3A22]"
                                >
                                    <LayoutDashboard className="w-4 h-4 mr-2" />
                                    Tableau de bord
                                </Button>
                            </Link>
                        ) : (
                            <Link to="/login">
                                <Button
                                    data-testid="public-login-btn"
                                    className="bg-[#B8934A] hover:bg-[#A07E36] text-white"
                                >
                                    <LogIn className="w-4 h-4 mr-2" />
                                    Accès membre
                                </Button>
                            </Link>
                        )}
                    </div>
                </div>
            </header>

            <main className="max-w-6xl mx-auto px-6 py-10 space-y-8">
                {/* Read-only notice */}
                {!token && (
                    <div className="flex items-center gap-3 text-sm text-[#5A6F45] bg-[#EDE7D3] border border-[#DDD3B9] rounded-md px-4 py-3">
                        <ShieldCheck className="w-4 h-4 shrink-0" />
                        <span>
                            Vue publique — quantités disponibles uniquement. Les prix,
                            transactions et coffre sont réservés aux membres connectés.
                        </span>
                    </div>
                )}

                {/* Metrics */}
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
                    <MetricTile
                        testid="public-metric-products"
                        label="Produits référencés"
                        value={totalProducts}
                        icon={<Package className="w-5 h-5" />}
                    />
                    <MetricTile
                        testid="public-metric-items"
                        label="Articles en stock"
                        value={totalItems}
                        icon={<Boxes className="w-5 h-5" />}
                    />
                    <MetricTile
                        testid="public-metric-low"
                        label="En stock bas"
                        value={lowStock}
                        icon={<AlertTriangle className="w-5 h-5" />}
                        accent={lowStock > 0 ? "rose" : "neutral"}
                    />
                </div>

                {/* Filters */}
                <div className="flex flex-wrap gap-3">
                    <Input
                        data-testid="public-search-input"
                        placeholder="Rechercher un produit…"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        className="max-w-sm h-11 bg-white"
                    />
                    <Select value={categoryFilter} onValueChange={setCategoryFilter}>
                        <SelectTrigger
                            data-testid="public-category-filter"
                            className="w-56 h-11 bg-white"
                        >
                            <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="all">Toutes catégories</SelectItem>
                            {categories.map((c) => (
                                <SelectItem key={c.id} value={c.id}>
                                    {c.name}
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                </div>

                {/* Products grid */}
                {loading ? (
                    <div className="text-slate-500 text-sm text-center py-10">
                        Chargement…
                    </div>
                ) : filtered.length === 0 ? (
                    <div className="text-slate-500 text-sm text-center py-16 border border-dashed border-slate-200 rounded-lg bg-white">
                        Aucun produit disponible.
                    </div>
                ) : (
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                        {filtered.map((it) => {
                            const color =
                                CATEGORY_COLORS[it.category_color] ||
                                CATEGORY_COLORS.slate;
                            return (
                                <Card
                                    key={it.id}
                                    data-testid={`public-product-${it.id}`}
                                    className="border-slate-200 shadow-sm p-5 bg-white hover:shadow-md transition-shadow"
                                >
                                    <div className="flex items-start justify-between gap-3">
                                        <div className="min-w-0">
                                            <div className="font-medium text-slate-900 truncate">
                                                {it.name}
                                            </div>
                                            <Badge
                                                variant="outline"
                                                className={`${color} border mt-2 font-medium`}
                                            >
                                                {it.category_name}
                                            </Badge>
                                        </div>
                                        <div className="text-right shrink-0">
                                            <div className="text-[10px] uppercase tracking-[0.15em] text-slate-500 font-semibold">
                                                Quantité
                                            </div>
                                            <div
                                                className={`font-display text-3xl font-bold tabular-nums mt-0.5 ${
                                                    it.low_stock
                                                        ? "text-rose-700"
                                                        : "text-slate-900"
                                                }`}
                                            >
                                                {it.quantity}
                                            </div>
                                            {it.low_stock && (
                                                <div className="text-[10px] font-semibold uppercase tracking-wide text-rose-700 mt-0.5">
                                                    Stock bas
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </Card>
                            );
                        })}
                    </div>
                )}
            </main>

            <footer className="max-w-6xl mx-auto px-6 pb-10 text-center text-xs text-slate-500">
                Raza Perdida MC - Vue du stock
            </footer>
        </div>
    );
}

function MetricTile({ label, value, icon, accent = "neutral", testid }) {
    const accentClasses =
        accent === "rose"
            ? "text-rose-700 bg-rose-100"
            : "text-[#7A6018] bg-[#F0E5C7]";
    return (
        <Card data-testid={testid} className="border-slate-200 shadow-sm p-6 bg-white">
            <div className="flex items-start justify-between">
                <div>
                    <div className="text-xs font-semibold uppercase tracking-[0.15em] text-slate-500">
                        {label}
                    </div>
                    <div className="font-display text-4xl font-bold tracking-tighter text-slate-900 mt-3 tabular-nums">
                        {value}
                    </div>
                </div>
                <div
                    className={`w-10 h-10 rounded-md flex items-center justify-center ${accentClasses}`}
                >
                    {icon}
                </div>
            </div>
        </Card>
    );
}
